/**
 * Staff — Recently Deleted Orders.
 *
 * Companion view to the Invoice Board Kanban. Any order the staff soft-deletes
 * lands here for 7 days; from this page they can:
 *   - Restore it (drops it back into whichever queue it came from)
 *   - Purge it (hard delete, gone for good)
 *
 * Backend endpoints used:
 *   GET  /api/staff/orders/recently-deleted
 *   POST /api/staff/orders/{product}/{id}/restore
 *   POST /api/staff/orders/{product}/{id}/purge
 *
 * The scheduler already auto-purges anything older than 7 days, so the "Days
 * left" chip is calculated client-side from `deleted_at`.
 */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Trash2, RotateCcw, AlertTriangle, Clock } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';
import useHomePath from '@/hooks/useHomePath';

const BRAND = {
  probound:       { label: 'ProBound',       colour: '#2F5E96', ink: '#fff' },
  prodecor:       { label: 'ProDecor',       colour: '#D5AB09', ink: '#2B2101' },
  proepoxy:       { label: 'ProEpoxy',       colour: '#7A0B7A', ink: '#fff' },
  material_order: { label: 'Material Order', colour: '#1f4030', ink: '#fff' },
};

const fmtGBP = (n) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format(n || 0);

// Deletions are purged automatically after 14 days by the backend scheduler.
const RETENTION_DAYS = 14;
const daysLeft = (deletedAt) => {
  if (!deletedAt) return null;
  const then = new Date(deletedAt).getTime();
  if (Number.isNaN(then)) return null;
  const elapsedDays = (Date.now() - then) / (1000 * 60 * 60 * 24);
  return Math.max(0, Math.ceil(RETENTION_DAYS - elapsedDays));
};

export default function RecentlyDeletedOrders() {
  const navigate = useNavigate();
  const homePath = useHomePath();
  const { user } = useContext(AuthContext);
  const canDelete = ['admin', 'manager'].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/orders/recently-deleted`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Failed to load');
      setRows(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!['admin', 'manager', 'team'].includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [user, navigate, load]);

  const restore = async (order) => {
    if (busyId) return;
    setBusyId(order.id);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/orders/${order.product}/${order.id}/restore`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Restore failed');
      toast.success(`${order.reference || 'Order'} restored`);
      setRows((rs) => rs.filter((r) => r.id !== order.id));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const purge = async () => {
    if (!purgeTarget || busyId) return;
    const order = purgeTarget;
    setBusyId(order.id);
    try {
      const res = await fetch(
        `${API_URL}/api/staff/orders/${order.product}/${order.id}/purge`,
        { method: 'POST', credentials: 'include' },
      );
      if (!res.ok) throw new Error(formatApiError(await res.json().catch(() => ({}))) || 'Purge failed');
      toast.success(`${order.reference || 'Order'} permanently deleted`);
      setRows((rs) => rs.filter((r) => r.id !== order.id));
      setPurgeTarget(null);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0f172a, #1e293b)' }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/staff/orders')}
            data-testid="recently-deleted-back"
            className="p-2 hover:bg-white/10 rounded-lg shrink-0"
            aria-label="Back to Invoice Board"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-bold flex items-center gap-2 truncate">
              <Trash2 className="w-5 h-5 shrink-0" /> Recently Deleted Orders
            </h1>
            <p className="hidden sm:block text-white/70 text-xs">
              Orders soft-deleted from the Invoice Board — kept for {RETENTION_DAYS} days, then permanently purged
            </p>
          </div>
          <Button
            onClick={() => navigate(homePath)}
            variant="outline"
            size="sm"
            className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0"
            aria-label="Home"
          >
            <Home className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-3">
        {loading ? (
          <Card className="p-10 text-center text-gray-500">Loading…</Card>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center" data-testid="recently-deleted-empty">
            <Trash2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="font-bold text-gray-900 mb-1">Nothing deleted</h3>
            <p className="text-gray-500 text-sm">
              Orders you delete from the Invoice Board will show up here for {RETENTION_DAYS} days.
            </p>
          </Card>
        ) : (
          rows.map((order) => {
            const brand = BRAND[order.product] || {};
            const left = daysLeft(order.deleted_at);
            const urgent = left !== null && left <= 1;
            return (
              <Card key={order.id} className="p-4" data-testid={`deleted-row-${order.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: brand.colour, color: brand.ink }}
                      >
                        {brand.label || order.product}
                      </span>
                      <span className="font-mono text-xs text-gray-700">{order.reference || order.id?.slice(0, 8)}</span>
                      <span
                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                          urgent ? 'bg-rose-100 text-rose-800' : 'bg-slate-100 text-slate-700'
                        }`}
                        data-testid={`deleted-ttl-${order.id}`}
                      >
                        <Clock className="w-3 h-3" />
                        {left === 0 ? 'Purging today' : `${left} day${left === 1 ? '' : 's'} left`}
                      </span>
                    </div>
                    <div className="font-semibold text-sm text-gray-900 mt-1 truncate">
                      {order.customer_reference || order.installer_name || 'No reference'}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {order.company_name ? `${order.company_name} · ` : ''}
                      {fmtGBP(order.total)} · Deleted {order.deleted_at ? new Date(order.deleted_at).toLocaleString('en-GB') : '—'}
                      {order.deleted_from_status ? ` · Was ${order.deleted_from_status}` : ''}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => restore(order)}
                      disabled={busyId === order.id || !canDelete}
                      data-testid={`restore-btn-${order.id}`}
                    >
                      <RotateCcw className="w-4 h-4 mr-1" /> Restore
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPurgeTarget(order)}
                      disabled={busyId === order.id || !canDelete}
                      className="text-rose-700 border-rose-200 hover:bg-rose-50"
                      data-testid={`purge-btn-${order.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete forever
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>

      <AlertDialog open={!!purgeTarget} onOpenChange={(o) => !o && setPurgeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" /> Delete forever?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{purgeTarget?.reference || 'this order'}</strong> from Ox Resin.
              It cannot be restored. Any linked Xero draft has already been voided when the order was first deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!busyId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={purge}
              disabled={!!busyId}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="purge-confirm"
            >
              Yes, delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
